/** @format */

import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { Popover } from "@headlessui/react";
import ReactFacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { t } from "i18next";
import Config, { SSO_META } from "config";
import { LoginType, WALLET_PROVIDER } from "interfaces";
import { api, ui } from "services";
import { useQuery } from "services/useQuery";
import { signInWithApple, signInWithGoogle } from "services/firebase";
import { ArtButton } from "components/button";
import { store } from "reducer/store";
import { setLoginType } from "reducer/logintype.slice";
import keplrService from "services/keplr";

export const SingleSignon = ({
  type,
  device,
}: {
  type: string;
  device: string;
}) => {
  const query = useQuery();
  const [isMetaMask, setIsMetaMask] = useState(true);
  const [openPopup, setOpenPopup] = useState("");

  const getReferralCode = () => {
    const code = query.get("code");
    const path = window.location.pathname;
    if (path !== "/") {
      return null;
    }
    return code;
  };

  const supportedAuthData = [
    {
      color: "#66452b",
      onRegisterClick: () => onMetaMaskClick(true, LoginType.BSC),
      onSignInClick: () => onMetaMaskClick(false, LoginType.BSC),
      authName: "BNB Chain",
      authExtension: "MetaMask",
      icon: "./assets/wallets/bsc.png",
      //@ts-ignore
      status: !!window.ethereum,
      extensionLink: Config.MetamaskExtension,
      disabled: Config.MainnetUnsupportedChain.includes(LoginType.BSC),
    },
    {
      color: "#814441",
      onRegisterClick: () => onMetaMaskClick(true, LoginType.Avalanche),
      onSignInClick: () => onMetaMaskClick(false, LoginType.Avalanche),
      authName: "AVAX Network",
      authExtension: "MetaMask",
      icon: "./assets/wallets/avalanche.png",
      //@ts-ignore
      status: !!window.ethereum,
      extensionLink: Config.MetamaskExtension,
      disabled: Config.MainnetUnsupportedChain.includes(LoginType.Avalanche),
    },
    // {
    //   color: "#4a689a",
    //   onRegisterClick: () => onMetaMaskClick(true, LoginType.Okex),
    //   onSignInClick: () => onMetaMaskClick(false, LoginType.Okex),
    //   authName: "OKX Chain",
    //   authExtension: "MetaMask",
    //   icon: "./assets/wallets/okex.png",
    //   //@ts-ignore
    //   status: !!window.ethereum,
    //   extensionLink: Config.MetamaskExtension,
    //   disabled: Config.MainnetUnsupportedChain.includes(LoginType.Okex),
    // },
    {
      color: "#5d4395",
      onRegisterClick: () => onKeplrClick(true, LoginType.Aura),
      onSignInClick: () => onKeplrClick(false, LoginType.Aura),
      authName: "Aura Network",
      authExtension: "Keplr",
      icon: "./assets/wallets/aura.png",
      //@ts-ignore
      status: !!window.keplr,
      extensionLink: Config.KeplrExtension,
      disabled: Config.MainnetUnsupportedChain.includes(LoginType.Aura),
    },
    {
      color: "#596189",
      onRegisterClick: () =>
        ui.openTextForm(
          "Sign up by email",
          registerByEmail,
          "Email",
          "email",
          "Confirm"
        ),
      onSignInClick: () => ui.openEmailForm(loginByEmail, "Sign in"),
      authName: "Email & Password",
      authExtension: "MetaMask",
      icon: "./assets/social/email.png",
      //@ts-ignore
      status: true,
      extensionLink: Config.MetamaskExtension,
      disabled: Config.MainnetUnsupportedChain.includes(LoginType.Email),
    },
  ];

  const supportedSocialAuth = {
    Facebook: {
      color: "#344763",
      onRegisterClick: async (accessToken: string) => {
        await onSocialClick(true, accessToken, LoginType.Facebook);
      },
      onSignInClick: async (accessToken: string) => {
        await onSocialClick(false, accessToken, LoginType.Facebook);
      },
      authName: "Facebook",
      icon: "./assets/social/facebook.png",
    },
    Google: {
      color: "#441f21",
      onRegisterClick: async (accessToken: string) => {
        await onSocialClick(true, accessToken, LoginType.Google);
      },
      onSignInClick: async (accessToken: string) => {
        await onSocialClick(false, accessToken, LoginType.Google);
      },
      authName: "Google",
      icon: "./assets/social/google.png",
      disabled: false,
      // disabled: Config.MainnetUnsupportedChain.includes(LoginType.Google)
    },
    Twitter: {
      color: "#325874",
      onRegisterClick: async (accessToken: string, secretToken: string) => {
        await onSocialClick(true, accessToken, LoginType.Twitter, secretToken);
      },
      onSignInClick: async (accessToken: string, secretToken: string) => {
        await onSocialClick(false, accessToken, LoginType.Twitter, secretToken);
      },
      authName: "Twitter",
      icon: "./assets/social/twitter.png",
    },
    Apple: {
      color: "#4b4a67",
      onRegisterClick: async (idToken: string) => {
        await onSocialClick(true, idToken, LoginType.Apple);
      },
      onSignInClick: async (idToken: string) => {
        await onSocialClick(false, idToken, LoginType.Apple);
      },
      authName: "Apple",
      icon: "./assets/social/apple.png",
    },
    // "Email": {
    //     color: "#596189",
    //     onRegisterClick: () => ui.openTextForm(
    //         'Sign up by email',
    //         registerByEmail,
    //         "Email",
    //         "email",
    //         "Confirm"
    //     ),
    //     onSignInClick: () => ui.openEmailForm(
    //         loginByEmail,
    //         "Sign in"
    //     ),
    //     authName: "Email & password",
    //     icon: "./assets/social/email.png",
    //     status: true,
    //     disabled: Config.MainnetUnsupportedChain.includes(LoginType.Email)
    // }
  };

  const onMetaMaskClick = async (isRegister = false, type: LoginType) => {
    if (isMetaMask) {
      try {
        if (isRegister) {
          const referral = getReferralCode();
          await api.registerMetaMask(referral, type);
        } else {
          await api.login(type);
          setOpenPopup("");
        }
        store.dispatch(setLoginType(type));
      } catch (err: any) {
        ui.alertFailed("", t(err.message));
      }
    } else {
      window.open(
        "https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn?hl=en",
        "_blank"
      );
    }
  };

  const onKeplrClick = async (isRegister = false, type: LoginType) => {
    await new Promise((resolve) => {
      ui.confirm(
        "To avoid future risks, you need to confirm that you are using a wallet with a RECOVERY PHRASE to link to your current account.",
        resolve
      );
    });
    // @ts-ignore
    if (window.keplr) {
      try {
        if (isRegister) {
          const referral = getReferralCode();
          await api.registerCosmos(referral, type);
        } else {
          await api.loginCosmos(type);
          setOpenPopup("");
        }
        store.dispatch(setLoginType(type));
      } catch (err: any) {
        ui.alertFailed("", t(err.message));
      }
    } else {
      window.open(Config.KeplrExtension, "_blank");
    }
  };

  const onSocialClick = async (
    isRegister = false,
    accessToken: string,
    loginType: LoginType,
    secretToken?: string
  ) => {
    try {
      if (isRegister) {
        const referral = getReferralCode();
        await api.registerBySocial(
          accessToken,
          loginType,
          referral,
          secretToken
        );
      } else {
        await api.loginBySocial(accessToken, loginType, secretToken);
        setOpenPopup("");
      }
    } catch (err: any) {
      ui.alertFailed("", t(err.message));
    }
  };

  const loginByEmail = async (email: string, password: string) => {
    api
      .loginByPassword(email, password)
      .then(() => {
        setOpenPopup("");
      })
      .catch((err: any) => {
        ui.alertFailed("", t(err.message));
      });
  };

  const registerByEmail = async (email: string) => {
    try {
      await api.sendRegisterEmail(email);
      ui.alertSuccess(
        "Send request successful !",
        "Please check your email to continue"
      );
    } catch (err: any) {
      ui.alertFailed("", t(err.message));
    }
  };

  useEffect(() => {
    if (
      //@ts-ignore
      typeof window.ethereum !== "undefined" ||
      //@ts-ignore
      typeof window.web3 !== "undefined"
    ) {
      setIsMetaMask(true);
    } else {
      setIsMetaMask(false);
    }
  }, []);

  if (device === "mobile") {
    return (
      <>
        <ArtButton
          color={type === "signup" ? "red" : "yellow"}
          className="w-[84px]"
          onClick={() => {
            setOpenPopup(type);
          }}
        >
          {type === "signup" ? "Sign up" : "Sign in"}
        </ArtButton>
        <Modal
          open={openPopup === type}
          style={{ width: "unset" }}
          closeIcon={
            <div className="text-red cursor-pointer float-right translate-x-2 -translate-y-2">
              <img
                src="/assets/images/icons/close-icon.svg"
                alt="close"
                className=""
              />
            </div>
          }
          onClose={() => {
            setOpenPopup("");
          }}
        >
          <div className="w-max-max md:w-[600px]">
            <div className="bg-black-linear-secondary py-3 md:py-6 px-8">
              <div className="text-center mb-5 text-white text-lg font-semibold">
                Continue with
              </div>
              {supportedAuthData.map((authData: any, index: number) => {
                return (
                  <div className="my-3 relative" key={`auth-${index}`}>
                    {authData.disabled && (
                      <div className="text-sm text-coming-soon absolute text-right w-full mt-[15px] pr-5">
                        Coming soon
                      </div>
                    )}
                    <Button
                      fluid
                      style={{
                        background: authData.color,
                        padding: "0 1rem",
                      }}
                      onClick={() => {
                        if (authData.isDevelop) {
                          ui.alertFailed(t("feature_under_development"), "");
                          return;
                        }
                        if (authData.status) {
                          if (type === "signup") {
                            authData.onRegisterClick();
                          } else {
                            authData.onSignInClick();
                          }
                        } else {
                          window.open(authData.extensionLink, "_blank");
                        }
                      }}
                      disabled={authData.disabled}
                    >
                      <div className="flex relative">
                        <img
                          src={require(`${authData.icon}`)}
                          className="float-left w-8 h-8 flex self-center"
                          alt="phantom"
                        />
                        <div className="flex flex-col relative mx-auto justify-center h-min-32">
                          <div className="text-md text-white font-medium">
                            {type === "signup" ? "Sign up" : "Sign in"} with{" "}
                            {authData.authName}
                          </div>
                          {authData?.authExtension && !authData?.status && (
                            <div className="text-sm text-red-2">
                              Download {authData.authExtension} extension
                            </div>
                          )}
                        </div>
                        {!authData.disabled && (
                          <div className="text-sm text-ready absolute text-right w-full mt-[15px]">
                            Ready
                          </div>
                        )}
                      </div>
                    </Button>
                  </div>
                );
              })}

              <div className="text-red text-center mt-4 mb-3">
                Extension of selected wallet will be installed
              </div>
              <div className="br mb-4"></div>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Popover>
        {({ open }) => (
          <>
            <Popover.Button>
              <span>
                <span className="pr-6 mr-4">
                  <ArtButton
                    color={type === "signup" ? "red" : "yellow"}
                    onClick={() => {
                      setOpenPopup(type);
                    }}
                  >
                    {type === "signup" ? "Sign up" : "Sign in"}
                  </ArtButton>
                </span>
              </span>
            </Popover.Button>
            <Popover.Panel>
              <div className="w-[520px] py-6 px-10 bg-black-linear-secondary absolute -translate-x-[430px] my-4">
                <div className="text-center mb-5 text-white text-lg font-semibold">
                  Continue with
                </div>
                {supportedAuthData.map((authData: any, index: number) => {
                  return (
                    <div className="my-3 relative" key={index}>
                      {authData.disabled && (
                        <div className="text-sm text-coming-soon absolute text-right w-full mt-[15px] pr-5">
                          Coming soon
                        </div>
                      )}
                      <Button
                        fluid
                        style={{
                          background: authData.color,
                          padding: "0 1rem",
                        }}
                        onClick={() => {
                          if (authData.isDevelop) {
                            ui.alertFailed(t("feature_under_development"), "");
                            return;
                          }
                          if (authData.status) {
                            if (type === "signup") {
                              authData.onRegisterClick();
                            } else {
                              authData.onSignInClick();
                            }
                          } else {
                            window.open(authData.extensionLink, "_blank");
                          }
                        }}
                        disabled={authData.disabled}
                      >
                        <div className="flex relative">
                          <img
                            src={require(`${authData.icon}`)}
                            className="float-left w-8 h-8 flex self-center"
                            alt="phantom"
                          />
                          <div className="flex flex-col relative ml-12 justify-center h-min-32">
                            <div className="text-md text-white font-medium">
                              {type === "signup" ? "Sign up" : "Sign in"} with{" "}
                              {authData.authName}
                            </div>
                            {authData?.authExtension && !authData?.status && (
                              <div className="text-sm text-red-2">
                                Download {authData.authExtension} extension
                              </div>
                            )}
                          </div>
                          {!authData.disabled && (
                            <div className="text-sm text-ready absolute text-right w-full mt-[15px]">
                              Ready
                            </div>
                          )}
                        </div>
                      </Button>
                    </div>
                  );
                })}

                <div className="text-red text-center mt-4 mb-3">
                  Extension of selected wallet will be installed
                </div>
                <div className="br mb-4"></div>
              </div>
            </Popover.Panel>
          </>
        )}
      </Popover>
    </>
  );
};
